import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

// Latest own listing fetch
export const FETCH_LATEST_OWN_LISTING_REQUEST = 'app/ProfileSettingsPage/FETCH_LATEST_OWN_LISTING_REQUEST';
export const FETCH_LATEST_OWN_LISTING_SUCCESS = 'app/ProfileSettingsPage/FETCH_LATEST_OWN_LISTING_SUCCESS';
export const FETCH_LATEST_OWN_LISTING_ERROR = 'app/ProfileSettingsPage/FETCH_LATEST_OWN_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
  latestOwnListing: null,
  latestOwnListingInProgress: false,
  latestOwnListingError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    case FETCH_LATEST_OWN_LISTING_REQUEST:
      return {
        ...state,
        latestOwnListingInProgress: true,
        latestOwnListingError: null,
      };
    case FETCH_LATEST_OWN_LISTING_SUCCESS:
      return {
        ...state,
        latestOwnListingInProgress: false,
        latestOwnListing: payload|| null,
      };
    case FETCH_LATEST_OWN_LISTING_ERROR:
      return {
        ...state,
        latestOwnListingInProgress: false,
        latestOwnListingError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

export const getOwnListingRequest = () => ({
  type: FETCH_LATEST_OWN_LISTING_REQUEST,
});

export const getOwnListingSuccess = result => ({
  type: FETCH_LATEST_OWN_LISTING_SUCCESS,
  payload: result,
});

export const getOwnListingError = error => ({
  type: FETCH_LATEST_OWN_LISTING_ERROR,
  payload: error,
  error: true,
});
// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));

        const userType = currentUser?.attributes?.profile?.publicData?.userType;
        if (userType !== 'student') {
          return sdk.ownListings
            .query({ page: 1, perPage: 10, sort: '-createdAt' })
            .then(ownListingsResponse => {
              const listings = ownListingsResponse?.data?.data || [];
              const country = currentUser?.attributes?.profile?.publicData?.country;
              const countryOfOrigin = currentUser?.attributes?.profile?.publicData?.countryOfOrigin;
              const rawTeachingLanguages = currentUser?.attributes?.profile?.publicData?.teachingLanguages;
              const normalizedTeachingLanguages = Array.isArray(rawTeachingLanguages)
                ? rawTeachingLanguages
                    .map(item =>
                      typeof item === 'string'
                        ? item
                        : item?.key || item?.value || item?.option
                    )
                    .filter(Boolean)
                : [];
              const rawDialects = currentUser?.attributes?.profile?.publicData?.dialects;
              const normalizedDialects = Array.isArray(rawDialects)
                ? rawDialects
                    .map(item =>
                      typeof item === 'string'
                        ? item
                        : item?.key || item?.value || item?.option
                    )
                    .filter(Boolean)
                : [];
              const primaryDialectCountry = normalizedDialects.length > 0 ? normalizedDialects[0] : null;

              const updatePromises = listings.map(listing => {
                const listingId = listing.id;
                const listingPublicData = listing.attributes.publicData || {};

                const countryChanged = country && listingPublicData.country !== country;
                const originChanged = countryOfOrigin && listingPublicData.countryOfOrigin !== countryOfOrigin;
                const currentListingLangs = Array.isArray(listingPublicData.teachingLanguages)
                  ? listingPublicData.teachingLanguages
                      .map(item =>
                        typeof item === 'string'
                          ? item
                          : item?.key || item?.value || item?.option
                      )
                      .filter(Boolean)
                  : [];
                const langsChanged =
                  JSON.stringify(currentListingLangs) !==
                  JSON.stringify(normalizedTeachingLanguages);
                const currentListingDialects = Array.isArray(listingPublicData.dialects)
                  ? listingPublicData.dialects
                      .map(item =>
                        typeof item === 'string'
                          ? item
                          : item?.key || item?.value || item?.option
                      )
                      .filter(Boolean)
                  : [];
                const dialectsChanged =
                  JSON.stringify(currentListingDialects) !==
                  JSON.stringify(normalizedDialects);

                if (countryChanged || originChanged || langsChanged || dialectsChanged) {
                  return sdk.ownListings.update({
                    id: listingId,
                    publicData: {
                      ...(country ? { country } : {}),
                      ...(countryOfOrigin ? { countryOfOrigin } : {}),
                      teachingLanguages: normalizedTeachingLanguages,
                      dialects: normalizedDialects,
                      ...(primaryDialectCountry ? { dialectCountry: primaryDialectCountry } : {}),
                    }
                  });
                }
                return Promise.resolve();
              });

              return Promise.all(updatePromises).then(() => currentUser);
            })
            .catch(err => {
              console.error('Failed to sync country to listing:', err);
              return currentUser;
            });
        }

        return currentUser;
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));

  };
};

// ================ Own listings helpers (passthrough) ================ //
// Lightweight thunk to fetch own listings from this duck without importing ManageListingsPage.duck
// Usage: dispatch(getOwnListings({ page: 1, perPage: 42, include: ['images'] }))

// Return the latest (newest) own listing entity or null if none exist
export const getLatestOwnListing = () => {
  return (dispatch, getState, sdk) => {
    dispatch(getOwnListingRequest())
    return sdk.ownListings
      .query({ page: 1, perPage: 1, sort: '-createdAt' })
      .then(resp => {
        const items = resp?.data?.data || [];
        const latest = items[0] || null;
        dispatch(getOwnListingSuccess(latest))
        return latest;
      })
      .catch(e => {
        const err = storableError(e);
        dispatch(getOwnListingError(err))
        throw err;
      });
  };
};
